import { useState, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { CloudArrowUp, Download, FileXls, CheckCircle, XCircle, Warning } from "@phosphor-icons/react";

const TEMPLATE_HEADERS = [
  "Name",
  "Email",
  "Password",
  "Department",
  "Role",          // viewer / editor / admin
  "TrainingRole",  // employee / department_head / training_admin
  "EmployeeCode",
];

const TEMPLATE_SAMPLE = [
  ["Jane Doe", "jane.doe@cmenvirosystems.com", "Welcome@123", "Operations", "editor", "employee", "EMP001"],
  ["John Smith", "john.smith@cmenvirosystems.com", "", "Sales", "admin", "department_head", "EMP002"],
];

function downloadTemplate() {
  const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, ...TEMPLATE_SAMPLE]);
  ws["!cols"] = TEMPLATE_HEADERS.map(() => ({ wch: 24 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Users");
  XLSX.writeFile(wb, "cme-peoplehub-users-template.xlsx");
}

function normalizeRow(row) {
  // Accept either snake_case or capitalized headers
  const get = (...keys) => {
    for (const k of keys) {
      if (row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== "") return String(row[k]).trim();
    }
    return "";
  };
  return {
    name: get("Name", "name", "Full Name", "full_name"),
    email: get("Email", "email", "E-mail"),
    password: get("Password", "password"),
    department: get("Department", "department", "Dept"),
    role: (get("Role", "role", "App Role") || "admin").toLowerCase(),
    training_role: (get("TrainingRole", "training_role", "Training Role") || "employee").toLowerCase().replace(/\s+/g, "_"),
    employee_code: get("EmployeeCode", "employee_code", "Emp Code", "Employee Code"),
  };
}

export default function BulkUserUploadDialog({ open, onOpenChange, onImported }) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [defaultPassword, setDefaultPassword] = useState("Welcome@123");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setRows([]); setFileName(""); setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      const normalized = json.map(normalizeRow).filter((r) => r.name || r.email);
      setRows(normalized);
      if (normalized.length === 0) {
        toast.error("No rows found. Make sure your file has Name and Email columns.");
      } else {
        toast.success(`${normalized.length} rows ready to import`);
      }
    } catch (err) {
      toast.error("Failed to parse file. Use .xlsx or .csv with the right columns.");
    }
  };

  const handleImport = async () => {
    if (rows.length === 0) {
      toast.error("Please upload a file first");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/users/bulk-import`,
        { users: rows, default_password: defaultPassword || "Welcome@123" },
        { withCredentials: true }
      );
      setResult(res.data);
      toast.success(`Imported ${res.data.created_count} users (${res.data.skipped_count} skipped, ${res.data.error_count} errors)`);
      onImported?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk import failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl" data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileXls size={22} weight="duotone" className="text-emerald-600" />
            Bulk Upload Users
          </DialogTitle>
          <DialogDescription>
            Upload an Excel (.xlsx) or CSV file with your team. Each user will get a unique QR code automatically. Existing emails are skipped.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-md bg-secondary/40 border border-border">
              <div className="text-sm">
                <p className="font-medium">Need a template?</p>
                <p className="text-muted-foreground text-xs">Download the Excel template with the correct columns and sample rows.</p>
              </div>
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn">
                <Download size={14} className="mr-2" /> Download template
              </Button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="bulk-file">Excel / CSV file</Label>
                <Input
                  id="bulk-file"
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFile}
                  data-testid="bulk-file-input"
                />
                {fileName && (
                  <p className="mt-1 text-xs text-muted-foreground">Loaded: <strong>{fileName}</strong> ({rows.length} rows)</p>
                )}
              </div>
              <div>
                <Label htmlFor="default-pw">Default password (used if row blank)</Label>
                <Input
                  id="default-pw"
                  value={defaultPassword}
                  onChange={(e) => setDefaultPassword(e.target.value)}
                  placeholder="Welcome@123"
                  data-testid="bulk-default-password"
                />
              </div>
            </div>

            {rows.length > 0 && (
              <div className="border border-border rounded-md">
                <ScrollArea className="h-64">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Row</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Training</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.slice(0, 100).map((r, i) => (
                        <TableRow key={i} data-testid={`bulk-preview-row-${i + 1}`}>
                          <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                          <TableCell className="font-medium">{r.name || <span className="text-destructive">— missing</span>}</TableCell>
                          <TableCell>{r.email || <span className="text-destructive">— missing</span>}</TableCell>
                          <TableCell>{r.department || "—"}</TableCell>
                          <TableCell><Badge variant="secondary">{r.role}</Badge></TableCell>
                          <TableCell><Badge variant="outline">{r.training_role}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
                {rows.length > 100 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground border-t bg-secondary/30">
                    Showing first 100 of {rows.length} rows. All rows will be imported.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-4" data-testid="bulk-upload-result">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-center">
                <CheckCircle size={20} weight="fill" className="text-emerald-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-emerald-700">{result.created_count}</p>
                <p className="text-xs text-emerald-700/80">Created</p>
              </div>
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-center">
                <Warning size={20} weight="fill" className="text-amber-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-amber-700">{result.skipped_count}</p>
                <p className="text-xs text-amber-700/80">Skipped (exists)</p>
              </div>
              <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3 text-center">
                <XCircle size={20} weight="fill" className="text-destructive mx-auto mb-1" />
                <p className="text-2xl font-bold text-destructive">{result.error_count}</p>
                <p className="text-xs text-destructive/80">Errors</p>
              </div>
            </div>

            {(result.errors?.length > 0 || result.skipped?.length > 0) && (
              <ScrollArea className="h-48 border border-border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.errors?.map((e, i) => (
                      <TableRow key={`err-${i}`}>
                        <TableCell>{e.row}</TableCell>
                        <TableCell>{e.email}</TableCell>
                        <TableCell className="text-destructive">{e.reason}</TableCell>
                      </TableRow>
                    ))}
                    {result.skipped?.map((s, i) => (
                      <TableRow key={`skip-${i}`}>
                        <TableCell>{s.row}</TableCell>
                        <TableCell>{s.email}</TableCell>
                        <TableCell className="text-amber-700">{s.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} data-testid="bulk-close-btn">
            {result ? "Done" : "Cancel"}
          </Button>
          {!result && (
            <Button onClick={handleImport} disabled={submitting || rows.length === 0} data-testid="bulk-import-btn">
              <CloudArrowUp size={16} className="mr-2" />
              {submitting ? "Importing..." : `Import ${rows.length || ""} users`}
            </Button>
          )}
          {result && (
            <Button onClick={reset} variant="default" data-testid="bulk-upload-another-btn">
              Upload another file
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
